import React, { useCallback, useEffect, useRef, useState } from 'react';
import { tl } from '../../utils/inlineI18n';
import { notifyBotBlocked } from '../BotBlockedOverlay';
import './BotMeter.css';

// Bot Ölçer + "BOT DEĞİLİM" doğrulaması. Seviye ve doğrulama sunucuda belirlenir;
// istemci sadece tıklama/tuş zamanlarını bildirir ve sunucunun durumunu gösterir.
const LEVELS = [
  { color: '#3ecf6a', label: () => tl('Normal', 'Normal') },
  { color: '#9be05a', label: () => tl('Düşük', 'Low') },
  { color: '#ffd23b', label: () => tl('Orta', 'Medium') },
  { color: '#ff9a2e', label: () => tl('Yüksek', 'High') },
  { color: '#ff4040', label: () => tl('Çok yüksek', 'Very high') },
];
const FLUSH_MS = 8000;
const MOVE_MS = 2600;

const nowMs = () => Math.round((performance.timeOrigin || Date.now() - performance.now()) + performance.now());

const tokenFor = (el) => {
  const btn = el?.closest?.('button,[role="button"],a,[data-bot-key]');
  if (!btn) return null;
  const key = btn.getAttribute('data-bot-key');
  if (key) return key.slice(0, 40);
  const cls = (typeof btn.className === 'string' ? btn.className.split(' ')[0] : '') || btn.tagName;
  const parent = btn.parentElement;
  const idx = parent ? Array.prototype.indexOf.call(parent.children, btn) : 0;
  return `${cls}#${idx}`.slice(0, 40);
};

// BOT DEĞİLİM butonu ekrandaki tıklanabilir öğelerden (saldırı, beceri, menü...) uzakta açılır;
// böylece alışkanlıkla ya da otomatik tıklayıcıyla aynı noktaya basmak onu bulamaz.
const BTN_W = 190;
const BTN_H = 70;
const KEEP_AWAY = 36;

const clickableRects = () => {
  const out = [];
  document.querySelectorAll('button, a, [role="button"], input, select, textarea, [data-bot-key], .menu-button, .monster-card, [class*="monster"]').forEach((el) => {
    if (el.closest('.bot-verify-layer')) return;
    const r = el.getBoundingClientRect();
    if (r.width < 4 || r.height < 4 || r.bottom < 0 || r.right < 0 || r.top > window.innerHeight || r.left > window.innerWidth) return;
    out.push(r);
  });
  return out;
};

const spotScore = (x, y, rects) => {
  // En yakın tıklanabilir öğeye olan mesafe (büyük = iyi).
  let best = Infinity;
  rects.forEach((r) => {
    const dx = Math.max(r.left - (x + BTN_W), x - r.right, 0);
    const dy = Math.max(r.top - (y + BTN_H), y - r.bottom, 0);
    best = Math.min(best, Math.hypot(dx, dy));
  });
  return best;
};

const pickSpot = (near = null) => {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const maxX = Math.max(8, w - BTN_W - 8);
  const minY = Math.min(150, h * 0.3); // üstteki açıklama kutusunun altı
  const maxY = Math.max(minY, h - BTN_H - 12);
  const rects = clickableRects();
  let best = null;
  for (let i = 0; i < 60; i += 1) {
    const x = near ? Math.min(maxX, Math.max(8, near.x + (Math.random() * 120 - 60))) : 8 + Math.random() * (maxX - 8);
    const y = near ? Math.min(maxY, Math.max(minY, near.y + (Math.random() * 120 - 60))) : minY + Math.random() * (maxY - minY);
    const score = spotScore(x, y, rects);
    if (!best || score > best.score) best = { x, y, score };
    if (score >= KEEP_AWAY && !near && i > 10) break;
  }
  // Yakınında güvenli yer yoksa ekranın başka bir yerine atla.
  if (near && best.score < KEEP_AWAY) return pickSpot(null);
  return { x: best.x, y: best.y };
};

const BotMeter = ({ socketRef, authed }) => {
  const [state, setState] = useState({ level: 1, pending: false, challenge: '', showMeter: false });
  const [spot, setSpot] = useState({ x: 40, y: 200 });
  const [toast, setToast] = useState('');
  const [verifying, setVerifying] = useState(false);
  const bufRef = useRef([]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !authed) return undefined;
    const off = socket.on('botCheck', (p) => {
      if (!p) return;
      if (p.blockedUntil) notifyBotBlocked(p.blockedUntil);
      setState({ level: p.level || 1, pending: !!p.pending, challenge: p.challenge || '', showMeter: !!p.showMeter, steadySec: p.steadySec || 0, score: p.score || 0, inputs: p.inputs || 0, recent: p.recent || '' });
    });
    socket.send('botStatus', {}, 'botCheck').then((p) => {
      if (p && typeof p.level === 'number') setState({ level: p.level || 1, pending: !!p.pending, challenge: p.challenge || '', showMeter: !!p.showMeter });
    }).catch(() => {});
    return off;
  }, [socketRef, authed]);

  // Tıklama ve tuş zamanlarını topla, birkaç saniyede bir sunucuya gönder.
  useEffect(() => {
    const push = (ev) => {
      const buf = bufRef.current;
      buf.push(ev);
      if (buf.length > 200) buf.splice(0, buf.length - 200);
    };
    // Bekleme süresinde kapalı (disabled) butonlarda bazı tarayıcılar pointerdown göndermez;
    // touchstart da dinlenir, aynı dokunuş 250 ms içinde tekrar sayılmaz.
    let lastAt = 0;
    const onPointer = (e) => {
      if (e.target?.closest?.('.bot-verify-layer')) return;
      const t = nowMs();
      if (t - lastAt < 250) return;
      const pt = e.touches?.[0] || e;
      const k = tokenFor(e.target) || `${e.target?.tagName || 'X'}`.slice(0, 40);
      lastAt = t;
      push({ t, k, x: Math.round(pt.clientX || 0), y: Math.round(pt.clientY || 0) });
    };
    const onKey = (e) => {
      if (e.repeat) return;
      push({ t: nowMs(), k: `K:${e.code || e.key}`.slice(0, 40) });
    };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('touchstart', onPointer, { capture: true, passive: true });
    document.addEventListener('keydown', onKey, true);
    const id = setInterval(() => {
      const socket = socketRef.current;
      if (!socket?.fire || bufRef.current.length === 0) return;
      const events = bufRef.current.splice(0, bufRef.current.length);
      socket.fire('botInput', { events });
    }, FLUSH_MS);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('touchstart', onPointer, { capture: true });
      document.removeEventListener('keydown', onKey, true);
      clearInterval(id);
    };
  }, [socketRef]);

  // Doğrulama açıkken buton birkaç saniyede bir yer değiştirir.
  useEffect(() => {
    if (!state.pending) return undefined;
    setSpot(pickSpot());
    const id = setInterval(() => {
      setSpot((s) => pickSpot(s));
    }, MOVE_MS);
    return () => clearInterval(id);
  }, [state.pending, state.challenge]);

  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(id);
  }, [toast]);

  const verify = useCallback(async () => {
    const socket = socketRef.current;
    if (!socket || verifying) return;
    setVerifying(true);
    try {
      const p = await socket.send('botVerify', { challenge: state.challenge }, 'botCheck');
      if (p && !p.pending) {
        setState({ level: p.level || 1, pending: false, challenge: '', showMeter: !!p.showMeter });
        setToast(tl('Doğrulama başarılı! Savaşına devam edebilirsin.', 'Verified! You can continue your battle.'));
      }
    } catch {
      /* sunucu yeni durumu botCheck ile gönderir */
    } finally {
      setVerifying(false);
    }
  }, [socketRef, state.challenge, verifying]);

  const level = Math.min(5, Math.max(1, state.level || 1));
  const cur = LEVELS[level - 1];

  return (
    <>
      {state.showMeter ? (
      <div className={`bot-meter bot-meter--l${level}`} title={tl('Bot Ölçer', 'Bot Meter')}>
        <span className="bot-meter-label">🤖 {tl('Bot Ölçer', 'Bot Meter')}</span>
        <div className="bot-meter-bar">
          {LEVELS.map((lv, i) => (
            <span
              key={i}
              className={`bot-meter-seg${i < level ? ' bot-meter-seg--on' : ''}`}
              style={{ '--seg-color': lv.color }}
            />
          ))}
        </div>
        <span className="bot-meter-level" style={{ color: cur.color }}>{cur.label()}</span>
        <span className="bot-meter-debug">
          {tl('Ritim', 'Rhythm')} {state.steadySec || 0}/90 sn · {tl('puan', 'score')} {state.score || 0} · {state.inputs || 0} {tl('tık', 'taps')}
          {state.recent ? <><br />{state.recent}</> : null}
        </span>
      </div>
      ) : null}

      {state.pending ? (
        <div className="bot-verify-layer" role="dialog" aria-modal="true">
          <div className="bot-verify-text">
            <strong>{tl('Muhafız Sorgusu', "Guard's Challenge")}</strong>
            <span>{tl('Olağandışı düzenli hareketler algılandı. Savaşa dönmek için mühürlü düğmeyi bul ve bas.', 'Unusually regular inputs detected. Find and press the sealed button to return to battle.')}</span>
          </div>
          <button
            type="button"
            className="bot-verify-btn"
            style={{ left: `${spot.x}px`, top: `${spot.y}px` }}
            onClick={verify}
            disabled={verifying}
          >
            <span className="bot-verify-btn-orn">⚔</span>
            <span className="bot-verify-btn-text">{tl('BOT DEĞİLİM', "I'M NOT A BOT")}</span>
            <span className="bot-verify-btn-orn">⚔</span>
          </button>
        </div>
      ) : null}

      {toast ? <div className="bot-verify-toast">{toast}</div> : null}
    </>
  );
};

export default BotMeter;
